import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useUserStore } from '@/stores/userStore'
import { useAuthStore } from '@/stores/authStore'
import { useBodyWeightStore } from '@/stores/bodyWeightStore'
import { useWorkoutStore, getNextWorkout } from '@/stores/workoutStore'
import { useCycleStore } from '@/stores/cycleStore'
import {
  getWorkoutTemplate,
  getWeekProgression,
  isDeloadWeek,
  type WorkoutType,
} from '@/data/xframe-templates'
import { startOfWeek } from 'date-fns'
import { cn } from '@/lib/utils'

// UI Components
import {
  Card,
  BentoCard,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card'
import {
  AnimatedNumber,
  Percentage,
  ProgressRing,
} from '@/components/ui/AnimatedNumber'
import {
  Skeleton,
  SkeletonChart,
} from '@/components/ui/Skeleton'
import {
  ChevronRightIcon,
  CalendarIcon,
  DumbbellIcon,
  FlaskIcon,
  ScaleIcon,
  CheckIcon,
} from '@/components/ui/Icons'
import { FadeTransition, AnimateOnView } from '@/components/ui/PageTransition'

// ============================================
// QUICK STATS ROW
// ============================================

function QuickStats() {
  const { profile } = useUserStore()
  const { sessions } = useWorkoutStore()
  const { getLatestWeight } = useBodyWeightStore()

  const latestWeight = getLatestWeight()
  const thisWeekWorkouts = sessions.filter((s) => {
    const sessionDate = new Date(s.date)
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
    return sessionDate >= weekStart && s.completed
  }).length

  const isMetric = profile?.unit_system === 'metric'
  const displayWeight = latestWeight
    ? isMetric
      ? latestWeight.toFixed(1)
      : (latestWeight * 2.20462).toFixed(1)
    : '--'

  return (
    <div className="grid grid-cols-3 gap-3">
      {/* Weight */}
      <Card className="relative overflow-hidden">
        <div className="flex items-center gap-2 mb-1">
          <ScaleIcon className="w-4 h-4 text-primary" />
          <span className="text-xs text-text-muted uppercase tracking-wide">Weight</span>
        </div>
        <div className="text-2xl font-bold text-text-primary tabular-nums">
          {displayWeight}
          <span className="text-sm font-normal text-text-muted ml-1">
            {isMetric ? 'kg' : 'lbs'}
          </span>
        </div>
        {/* Decorative gradient */}
        <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-primary/5 rounded-full blur-xl" />
      </Card>

      {/* This Week */}
      <Card className="relative overflow-hidden">
        <div className="flex items-center gap-2 mb-1">
          <DumbbellIcon className="w-4 h-4 text-success" />
          <span className="text-xs text-text-muted uppercase tracking-wide">This Week</span>
        </div>
        <div className="text-2xl font-bold text-text-primary">
          <AnimatedNumber value={thisWeekWorkouts} />
          <span className="text-sm font-normal text-text-muted ml-1">workouts</span>
        </div>
        <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-success/5 rounded-full blur-xl" />
      </Card>

      {/* Phase Progress */}
      <Card className="relative overflow-hidden">
        <div className="flex items-center gap-2 mb-1">
          <CalendarIcon className="w-4 h-4 text-accent" />
          <span className="text-xs text-text-muted uppercase tracking-wide">Phase</span>
        </div>
        <div className="flex items-center gap-2">
          <ProgressRing
            progress={profile ? (profile.current_week / 8) * 100 : 0}
            size={36}
            strokeWidth={3}
            showValue={false}
            color="stroke-accent"
          />
          <div>
            <div className="text-lg font-bold text-text-primary">
              {profile?.current_phase || '-'}
            </div>
            <div className="text-xs text-text-muted">
              Week {profile?.current_week || '-'}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

// ============================================
// PROGRAM STATUS CARD (Bento Large)
// ============================================

function ProgramStatusCard() {
  const { profile } = useUserStore()

  if (!profile) {
    return <SkeletonChart height="h-40" />
  }

  const isDeload = isDeloadWeek(profile.current_week as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8)
  const progressPercent = (profile.current_week / 8) * 100
  const progression = getWeekProgression(
    profile.current_phase as 1 | 2,
    profile.current_week as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  )

  return (
    <BentoCard span="double" className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />

      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-text-muted uppercase tracking-wide">Program Status</span>
              {isDeload && (
                <span className="px-2 py-0.5 text-[10px] font-bold bg-warning/20 text-warning rounded-full uppercase">
                  Deload
                </span>
              )}
            </div>
            <h2 className="text-3xl font-bold text-text-primary">
              Phase {profile.current_phase}
            </h2>
          </div>

          <div className="text-right">
            <div className="text-sm text-text-muted">Volume Tier</div>
            <div className="text-2xl font-bold text-primary">{profile.volume_tier}</div>
          </div>
        </div>

        {/* Week Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-text-secondary">Week {profile.current_week} of 8</span>
            <span className="text-text-muted">{Math.round(progressPercent)}%</span>
          </div>
          <div className="h-2 bg-elevated rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* RIR Targets */}
        <div className="flex gap-4">
          <div className="flex-1 p-3 bg-elevated/50 rounded-xl">
            <div className="text-xs text-text-muted mb-1">Compound RIR</div>
            <div className="text-xl font-bold text-text-primary">{progression.rir.compound}</div>
          </div>
          <div className="flex-1 p-3 bg-elevated/50 rounded-xl">
            <div className="text-xs text-text-muted mb-1">Isolation RIR</div>
            <div className="text-xl font-bold text-text-primary">{progression.rir.isolation}</div>
          </div>
          {progression.intensifier && (
            <div className="flex-1 p-3 bg-primary/10 rounded-xl">
              <div className="text-xs text-primary mb-1">Intensifier</div>
              <div className="text-sm font-bold text-primary capitalize">
                {progression.intensifier.replace('_', '-')}
              </div>
            </div>
          )}
        </div>
      </div>
    </BentoCard>
  )
}

// ============================================
// TODAY'S WORKOUT CARD
// ============================================

function TodaysWorkoutCard() {
  const { profile } = useUserStore()
  const { sessions, getCompletedDatesThisWeek } = useWorkoutStore()

  if (!profile) return null

  const completedThisWeek = getCompletedDatesThisWeek()
  const completedWorkoutTypes = sessions
    .filter((s) => completedThisWeek.includes(s.date.split('T')[0]))
    .map((s) => s.workout_type as WorkoutType)

  const nextWorkout = getNextWorkout(
    profile.volume_tier as 1 | 2 | 3,
    completedWorkoutTypes,
    profile.current_phase as 1 | 2
  )

  if (!nextWorkout) {
    return (
      <BentoCard variant="highlight" className="flex flex-col items-center justify-center py-8">
        <div className="w-12 h-12 bg-success/20 rounded-full flex items-center justify-center mb-3">
          <CheckIcon className="w-6 h-6 text-success" />
        </div>
        <h3 className="font-semibold text-text-primary mb-1">Week Complete!</h3>
        <p className="text-sm text-text-muted text-center">
          Great work! Rest up for next week.
        </p>
      </BentoCard>
    )
  }

  const template = getWorkoutTemplate(profile.current_phase as 1 | 2, nextWorkout.workoutType)
  const totalSets = template.exercises.reduce((sum, e) => sum + e.sets, 0)

  return (
    <BentoCard variant="highlight" className="relative overflow-hidden group">
      <Link to={`/logbook?workout=${nextWorkout.workoutType}`} className="block">
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />

        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-xs text-primary uppercase tracking-wide font-semibold">
                Up Next
              </span>
              <h3 className="text-xl font-bold text-text-primary">{nextWorkout.displayName}</h3>
            </div>
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <ChevronRightIcon className="w-6 h-6 text-white" />
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm text-text-muted">
            <span>{template.exercises.length} exercises</span>
            <span>·</span>
            <span>~{totalSets} sets</span>
          </div>

          {/* Exercise preview pills */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {template.exercises.slice(0, 4).map((exercise) => (
              <span
                key={exercise.id}
                className="px-2 py-0.5 bg-surface/80 text-text-secondary text-xs rounded-full"
              >
                {exercise.name.split(' ').slice(0, 2).join(' ')}
              </span>
            ))}
            {template.exercises.length > 4 && (
              <span className="px-2 py-0.5 text-text-muted text-xs">
                +{template.exercises.length - 4} more
              </span>
            )}
          </div>
        </div>
      </Link>
    </BentoCard>
  )
}

// ============================================
// WEEKLY CALENDAR
// ============================================

function WeeklyCalendar() {
  const { getCompletedDatesThisWeek } = useWorkoutStore()
  const completedDates = getCompletedDatesThisWeek()

  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  const today = new Date()
  const dayOfWeek = today.getDay()
  const todayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1

  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  const weekDates = days.map((_, i) => {
    const date = new Date(today)
    date.setDate(today.getDate() + mondayOffset + i)
    return date.toISOString().split('T')[0]
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">This Week</CardTitle>
      </CardHeader>

      <div className="flex justify-between gap-1">
        {days.map((day, index) => {
          const isToday = index === todayIndex
          const isCompleted = completedDates.includes(weekDates[index])
          const isPast = index < todayIndex

          return (
            <div
              key={`${day}-${index}`}
              className={cn(
                'flex-1 flex flex-col items-center py-2 rounded-xl transition-all',
                isToday && 'bg-primary/10'
              )}
            >
              <span
                className={cn(
                  'text-[10px] font-medium uppercase',
                  isToday ? 'text-primary' : 'text-text-muted'
                )}
              >
                {day}
              </span>
              <div
                className={cn(
                  'w-8 h-8 rounded-full mt-1.5 flex items-center justify-center transition-all',
                  isCompleted
                    ? 'bg-success text-white'
                    : isToday
                    ? 'bg-primary/20 ring-2 ring-primary ring-offset-2 ring-offset-surface'
                    : isPast
                    ? 'bg-elevated text-text-muted'
                    : 'bg-elevated/50 text-text-muted'
                )}
              >
                {isCompleted ? (
                  <CheckIcon className="w-4 h-4" />
                ) : (
                  <span className="text-xs font-medium">
                    {new Date(weekDates[index]).getDate()}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <p className="text-xs text-text-muted mt-3 text-center">
        {completedDates.length}/6 workouts this week
      </p>
    </Card>
  )
}

// ============================================
// BODY WEIGHT CHART
// ============================================

function BodyWeightChart() {
  const { profile } = useUserStore()
  const { user } = useAuthStore()
  const { logs, loading, loadLogs, addLog, getTodayLog, getLatestWeight } = useBodyWeightStore()
  const [showQuickLog, setShowQuickLog] = useState(false)
  const [weightInput, setWeightInput] = useState('')

  useEffect(() => {
    if (user?.id) {
      loadLogs(user.id, 30)
    }
  }, [user?.id, loadLogs])

  const isMetric = profile?.unit_system === 'metric'
  const todayLog = getTodayLog()
  const latestWeight = getLatestWeight()

  const displayWeight = (kg: number) => (isMetric ? kg.toFixed(1) : (kg * 2.20462).toFixed(1))
  const parseInputWeight = (input: string): number | null => {
    const value = parseFloat(input)
    if (isNaN(value)) return null
    return isMetric ? value : value / 2.20462
  }

  const handleQuickLog = async () => {
    if (!user?.id || !weightInput) return
    const weightKg = parseInputWeight(weightInput)
    if (weightKg === null || weightKg <= 0) return

    await addLog(user.id, weightKg)
    setWeightInput('')
    setShowQuickLog(false)
  }

  // Calculate trend
  const chartData = [...logs].reverse().slice(-14)
  const trend = useMemo(() => {
    if (chartData.length < 7) return 0
    const recent = chartData.slice(-7)
    const older = chartData.slice(0, 7)
    const recentAvg = recent.reduce((sum, l) => sum + l.weight_kg, 0) / recent.length
    const olderAvg = older.reduce((sum, l) => sum + l.weight_kg, 0) / older.length
    return ((recentAvg - olderAvg) / olderAvg) * 100
  }, [chartData])

  if (loading) {
    return <SkeletonChart height="h-36" />
  }

  // SVG Chart dimensions
  const width = 300
  const height = 80
  const padding = { top: 8, right: 8, bottom: 8, left: 8 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  const minWeight = chartData.length > 0 ? Math.min(...chartData.map((l) => l.weight_kg)) * 0.99 : 0
  const maxWeight = chartData.length > 0 ? Math.max(...chartData.map((l) => l.weight_kg)) * 1.01 : 100
  const weightRange = maxWeight - minWeight || 1

  const points = chartData.map((log, i) => ({
    x: padding.left + (i / Math.max(chartData.length - 1, 1)) * chartWidth,
    y: padding.top + chartHeight - ((log.weight_kg - minWeight) / weightRange) * chartHeight,
  }))

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaPath = `${linePath} L ${points[points.length - 1]?.x || 0} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`

  return (
    <Card>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Body Weight</CardTitle>
            {todayLog && (
              <span className="px-1.5 py-0.5 bg-success/20 text-success text-[10px] rounded-full">
                Logged
              </span>
            )}
          </div>
          {latestWeight && (
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold text-text-primary tabular-nums">
                {displayWeight(latestWeight)}
              </span>
              <span className="text-sm text-text-muted">{isMetric ? 'kg' : 'lbs'}</span>
              {trend !== 0 && (
                <Percentage value={trend} showTrend className="text-xs" />
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowQuickLog(!showQuickLog)}
          className="text-sm text-primary font-medium hover:text-primary-hover transition-colors"
        >
          {todayLog ? 'Update' : '+ Log'}
        </button>
      </div>

      {showQuickLog && (
        <div className="flex gap-2 mb-4 animate-fade-in">
          <input
            type="number"
            step="0.1"
            value={weightInput}
            onChange={(e) => setWeightInput(e.target.value)}
            placeholder={`Weight (${isMetric ? 'kg' : 'lbs'})`}
            className="input flex-1 py-2"
            autoFocus
          />
          <button onClick={handleQuickLog} disabled={!weightInput} className="btn-primary px-4 py-2">
            Save
          </button>
        </div>
      )}

      {chartData.length > 0 ? (
        <div className="h-20">
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
              </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#weightGradient)" />
            <path d={linePath} fill="none" stroke="#0ea5e9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            {points.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 4 : 2} fill="#0ea5e9" />
            ))}
          </svg>
        </div>
      ) : (
        <div className="h-20 flex items-center justify-center text-text-muted">
          <p className="text-sm">No weight data yet</p>
        </div>
      )}
    </Card>
  )
}

// ============================================
// CYCLE STATUS CARD
// ============================================

function CycleStatusCard() {
  const { profile } = useUserStore()
  const { activeCycle, cycleCompounds, loadCycles } = useCycleStore()

  useEffect(() => {
    if (profile?.id) {
      loadCycles(profile.id)
    }
  }, [profile?.id, loadCycles])

  if (!activeCycle) {
    return (
      <Card className="relative overflow-hidden">
        <Link to="/cycle" className="block group">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-elevated rounded-xl flex items-center justify-center">
              <FlaskIcon className="w-5 h-5 text-text-muted" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-text-primary group-hover:text-primary transition-colors">
                No Active Cycle
              </h3>
              <p className="text-sm text-text-muted">Tap to plan a cycle</p>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" />
          </div>
        </Link>
      </Card>
    )
  }

  const startDate = new Date(activeCycle.start_date)
  const today = new Date()
  const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
  const currentWeek = Math.floor(daysSinceStart / 7) + 1
  const cycleLength = cycleCompounds.length > 0 ? Math.max(...cycleCompounds.map((c) => c.endWeek)) : 12
  const progressPercent = Math.min(100, Math.round((currentWeek / cycleLength) * 100))

  return (
    <Card className="relative overflow-hidden">
      <Link to="/cycle" className="block group">
        {/* Gradient accent */}
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary to-accent rounded-l-xl" />

        <div className="pl-3">
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className="text-xs text-text-muted uppercase tracking-wide">Active Cycle</span>
              <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
                {activeCycle.name}
              </h3>
            </div>
            <span className="px-2 py-0.5 bg-success/20 text-success text-xs rounded-full font-medium">
              Week {currentWeek}
            </span>
          </div>

          {/* Mini progress */}
          <div className="h-1.5 bg-elevated rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Compounds */}
          {cycleCompounds.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {cycleCompounds.slice(0, 3).map((cc) => (
                <span key={cc.id} className="px-2 py-0.5 bg-elevated text-text-secondary text-xs rounded-full">
                  {cc.compound.shortName}
                </span>
              ))}
              {cycleCompounds.length > 3 && (
                <span className="px-2 py-0.5 text-text-muted text-xs">+{cycleCompounds.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </Link>
    </Card>
  )
}

// ============================================
// RECENT WORKOUTS
// ============================================

function RecentWorkouts() {
  const { sessions, loading } = useWorkoutStore()
  const recentCompleted = sessions.filter((s) => s.completed).slice(0, 5)

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  const getWorkoutDisplayName = (type: string) => {
    const names: Record<string, string> = {
      chest_push: 'Chest Push',
      pull_a: 'Pull A',
      legs: 'Legs',
      shoulder_push: 'Shoulder Push',
      pull_b: 'Pull B',
      ham_legs: 'Ham Legs',
    }
    return names[type] || type
  }

  return (
    <Card>
      <CardHeader
        action={
          <Link to="/logbook" className="text-sm text-primary hover:text-primary-hover transition-colors">
            View all
          </Link>
        }
      >
        <CardTitle className="text-base">Recent Workouts</CardTitle>
      </CardHeader>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : recentCompleted.length > 0 ? (
        <div className="space-y-1">
          {recentCompleted.map((session) => (
            <AnimateOnView key={session.id}>
              <div
                className={cn(
                  'flex items-center justify-between py-2.5 px-3 -mx-3 rounded-lg',
                  'hover:bg-elevated/50 transition-colors cursor-pointer'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <DumbbellIcon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">
                      {getWorkoutDisplayName(session.workout_type)}
                    </p>
                    <p className="text-xs text-text-muted">{formatDate(session.date)}</p>
                  </div>
                </div>
                {session.total_volume_kg && (
                  <span className="text-sm text-text-secondary tabular-nums">
                    {Math.round(session.total_volume_kg).toLocaleString()} kg
                  </span>
                )}
              </div>
            </AnimateOnView>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-text-muted">
          <DumbbellIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No workouts logged yet</p>
        </div>
      )}
    </Card>
  )
}

// ============================================
// MAIN DASHBOARD
// ============================================

export default function Dashboard() {
  const { user } = useAuthStore()
  const { loadSessions } = useWorkoutStore()

  useEffect(() => {
    if (user?.id) {
      loadSessions(user.id)
    }
  }, [user?.id, loadSessions])

  return (
    <FadeTransition>
      <div className="p-4 pb-24 space-y-4">
        {/* Quick Stats Row */}
        <QuickStats />

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Large: Program Status */}
          <ProgramStatusCard />

          {/* Medium: Today's Workout */}
          <TodaysWorkoutCard />
        </div>

        {/* Calendar + Weight */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <WeeklyCalendar />
          <BodyWeightChart />
        </div>

        {/* Cycle + Recent */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CycleStatusCard />
          <RecentWorkouts />
        </div>
      </div>
    </FadeTransition>
  )
}
